'use client';

import { Box, Grid, Typography, Chip, Paper, Stack } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CurrencyYenIcon from '@mui/icons-material/CurrencyYen';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslations } from 'next-intl';

export interface Metric {
    label: string;
    value: string;
    icon: 'trend' | 'star' | 'time' | 'money' | 'people' | 'check';
    color?: string;
}

export interface CaseStudyContent {
    title: string;
    descriptions: string[];
}

export interface CaseStudyProps {
    title: string;
    subtitle: string;
    chipLabel: string;
    contents: CaseStudyContent[];
    metrics: Metric[];
}

const IconMap = {
    trend: TrendingUpIcon,
    star: StarIcon,
    time: AccessTimeIcon,
    money: CurrencyYenIcon,
    people: PeopleIcon,
    check: CheckCircleOutlineIcon,
};

const CaseStudyCard = ({ title, subtitle, chipLabel, contents, metrics }: CaseStudyProps) => {
    const t = useTranslations('CaseStudiesPage.card');
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 3, md: 5 },
                borderRadius: '24px',
                border: '1px solid #e2e8f0',
                backgroundColor: 'white',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                mb: 4,
            }}
        >
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h4" component="h3" sx={{ fontWeight: 800, color: '#132c4b', mb: 1, lineHeight: 1.4 }}>
                            {title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                            {subtitle}
                        </Typography>
                    </Box>

                    <Chip
                        label={chipLabel}
                        size="small"
                        icon={<PeopleIcon sx={{ fontSize: '1rem !important' }} />}
                        sx={{
                            alignSelf: 'flex-start',
                            backgroundColor: '#fee2e2',
                            color: '#ef4444',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            '& .MuiChip-icon': { color: '#ef4444' }
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                    <Box sx={{
                        borderLeft: '4px solid #F15859',
                        pl: 3,
                        py: 0.5,
                        mt: 'auto',
                        mb: { xs: 4, md: 0 }
                    }}>
                        <>
                            {contents.map((content, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
                                        {content.title}
                                    </Typography>
                                    {
                                        content.descriptions.length > 1 ? (
                                            <ul className='list-disc pl-5 mt-2 text-sm' style={{ color: '#475569', lineHeight: 1.8 }}>
                                                {content.descriptions.map((description, index) => (
                                                    <li key={index}>{description}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.8 }}>
                                                {content.descriptions}
                                            </Typography>
                                        )
                                    }
                                </Box>
                            ))}
                        </>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 3 }}>
                        {t('results_label')} :
                    </Typography>
                    <Grid container spacing={2}>
                        {metrics.map((metric, index) => {
                            const IconComponent = IconMap[metric.icon];
                            return (
                                <Grid size={{ xs: 12, sm: 6, md: 6 }} key={index}>
                                    <Box sx={{
                                        p: 3,
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '16px',
                                        height: '100%'
                                    }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                            <IconComponent sx={{ fontSize: '1.2rem', color: metric.color || '#F15859' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#64748b' }}>
                                                {metric.label}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="h4" component="p" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                            {metric.value}
                                        </Typography>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default CaseStudyCard;
